import { Download } from 'lucide-react';
import { useId, type ReactElement } from 'react';

import { plural } from '@/lib/plural';

import type { ParsedSheet } from '../parse-sheet';
import type { SheetProblem } from '../types';

// Paso 1 de "Importar desde Excel": plantilla, origen de los datos (queda en la bitácora
// de carga histórica) y el archivo. Lo que no se puede leer se lista con fila y columna.

const SHOWN = 50;

function problemText(p: SheetProblem): string {
  if (p.row === null) {
    return p.message;
  }
  return p.column === null
    ? `Fila ${p.row}: ${p.message}`
    : `Fila ${p.row} · ${p.column}: ${p.message}`;
}

export function ImportFileStep({
  source,
  onSource,
  parsed,
  fileName,
  reading,
  onFile,
  onDownload,
}: {
  source: string;
  onSource: (v: string) => void;
  parsed: ParsedSheet | null;
  fileName: string | null;
  reading: boolean;
  onFile: (f: File) => void;
  onDownload: () => void;
}): ReactElement {
  const sourceId = useId();
  const fileId = useId();
  const problems = parsed?.problems ?? [];

  return (
    <div className="form-grid">
      <div className="fld">
        <span className="fld-lab">Plantilla</span>
        <p className="soft" style={{ margin: 0, fontSize: 13 }}>
          Llena la hoja Documentos; la hoja Instrucciones explica cada columna.
        </p>
        <div>
          <button type="button" className="btn mut sm" onClick={onDownload}>
            <Download size={14} aria-hidden="true" />
            Descargar plantilla
          </button>
        </div>
      </div>
      <div className="fld">
        <label htmlFor={sourceId} className="fld-lab">
          Origen de los datos
        </label>
        <input
          id={sourceId}
          className="selector fw"
          value={source}
          maxLength={150}
          placeholder="Ej. OneDrive de Calidad, octubre 2026"
          autoComplete="off"
          onChange={(e) => onSource(e.target.value)}
        />
      </div>
      <div className="fld">
        <label htmlFor={fileId} className="fld-lab">
          Archivo de Excel (.xlsx)
        </label>
        <input
          id={fileId}
          type="file"
          className="selector fw"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          disabled={reading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) {
              onFile(f);
            }
          }}
        />
      </div>
      {reading && (
        <p className="soft" aria-busy="true" style={{ margin: 0, fontSize: 13 }}>
          Leyendo el archivo…
        </p>
      )}
      {!reading && parsed !== null && problems.length === 0 && fileName !== null && (
        <p className="soft" role="status" style={{ margin: 0, fontSize: 13 }}>
          {plural(parsed.rows.length, 'documento leído', 'documentos leídos')} de {fileName}
        </p>
      )}
      {!reading && problems.length > 0 && (
        <div className="fld-error" role="alert">
          <p style={{ margin: 0 }}>Corrige el archivo y súbelo de nuevo:</p>
          <ul className="import-problems">
            {problems.slice(0, SHOWN).map((p, i) => (
              <li key={i}>{problemText(p)}</li>
            ))}
          </ul>
          {problems.length > SHOWN && <p style={{ margin: 0 }}>y {problems.length - SHOWN} más.</p>}
        </div>
      )}
    </div>
  );
}
